import {
  PayrollFileColumnTransformation,
  PayrollFileValueTransformation,
  PayrollFileRow,
  PayrollFileColumnTransformationColumn,
} from '@api/lib/payrollProcessing/types';
import {ExtractedColumn, ExtractedFile} from '@client/pages/payroll/types';
import {
  applyColumnTransformation,
  applyValueTransformations,
  convertAccountingFormat,
  evalTransformationFormula,
  getColumnValueWithFallback,
  isBlankLikeValue,
} from '@client/pages/payroll/utils/transformationUtils';

/**
 * Get a preview of the transformation result
 */
export function getTransformationPreview(
  columnTransformation: PayrollFileColumnTransformation,
  valueTransformations: PayrollFileValueTransformation[],
  fileRowsMap: Record<string, PayrollFileRow[]>,
): {
  preTransformation: string;
  postTransformation: string;
} {
  // Build a pre-transformation string that reflects the raw input values from the selected columns
  // so users can see the inputs to the column transformation (comma-separated when multiple).
  let preTransformation = '';
  if (columnTransformation?.type === 'NONE' && columnTransformation.column) {
    const fileRows = fileRowsMap[columnTransformation.column.fileId];
    if (fileRows && fileRows.length > 0) {
      preTransformation = String(fileRows[0][columnTransformation.column.name] ?? '');
    }
  } else if (
    columnTransformation?.type !== 'NONE' &&
    'columns' in columnTransformation &&
    columnTransformation.columns &&
    columnTransformation.columns.length > 0
  ) {
    const columns = columnTransformation.columns || [];
    const values = columns.map((col: PayrollFileColumnTransformationColumn) =>
      getColumnValueWithFallback(fileRowsMap, col),
    );
    preTransformation = values.filter((v: string) => v !== '').join(', ');
  } else if (columnTransformation?.type !== 'NONE' && 'pivotColumn' in columnTransformation) {
    // Pivot column - show the key value as the pre-transformation preview
    preTransformation = (
      columnTransformation as {type: 'PIVOT_COLUMN'; pivotColumn: {name: string}}
    ).pivotColumn.name;
  } else if (columnTransformation?.type === 'STATIC_VALUE') {
    preTransformation = columnTransformation.staticValue;
  }

  // Column transformation output before value transformations
  const columnOutput = applyColumnTransformation(columnTransformation, fileRowsMap);
  const postTransformation = applyValueTransformations(columnOutput, valueTransformations);

  return {
    preTransformation: truncatePreviewValue(preTransformation),
    postTransformation: truncatePreviewValue(postTransformation),
  };
}

export function buildPreviewContext(extracted: ExtractedFile[]): {
  fileRowsMap: Record<string, PayrollFileRow[]>;
  sampleRows: PayrollFileRow[];
} {
  const fileRowsMap: Record<string, PayrollFileRow[]> = {};
  for (const file of extracted) {
    const row: PayrollFileRow = {};
    for (const col of file.columns) {
      const firstNonEmpty = (col.samples || []).find(
        (s) => String(s ?? '').trim().length > 0 && !isBlankLikeValue(String(s ?? '')),
      );
      row[col.name] = firstNonEmpty ?? '';
    }
    fileRowsMap[file.file.fileId] = [row];
  }
  const sampleRows: PayrollFileRow[] = extracted.length > 0 ? [{}] : [];
  return {fileRowsMap, sampleRows};
}

export function calculateTransformationPreviewFromExtracted(
  mapping:
    | {
        columnTransformation: PayrollFileColumnTransformation;
        valueTransformations?: PayrollFileValueTransformation[];
      }
    | null
    | undefined,
  extracted: ExtractedFile[],
): {pre: string; post: string} {
  if (!mapping?.columnTransformation) return {pre: '', post: ''};
  const {fileRowsMap} = buildPreviewContext(extracted);
  const preview = getTransformationPreview(
    mapping.columnTransformation,
    mapping.valueTransformations || [],
    fileRowsMap,
  );
  return {pre: preview.preTransformation, post: preview.postTransformation};
}

function truncatePreviewValue(value: string, maxLength: number = 500): string {
  return value.length >= maxLength ? value.slice(0, maxLength - 4) + '…' : value;
}

export function calculateTransformationPreview(
  mapping:
    | {
        columnTransformation: PayrollFileColumnTransformation;
        valueTransformations?: PayrollFileValueTransformation[];
      }
    | null
    | undefined,
  columnsByFileId: Record<string, ExtractedColumn[]>,
) {
  if (!mapping?.columnTransformation) return {pre: '', post: ''};

  let preValue = '';
  let postValue = '';

  // Get sample data based on transformation type
  if (mapping.columnTransformation.type === 'STATIC_VALUE') {
    preValue = mapping.columnTransformation.staticValue;
    postValue = mapping.columnTransformation.staticValue;
  } else if (mapping.columnTransformation.type === 'NONE' && mapping.columnTransformation.column) {
    const columnIndex = mapping.columnTransformation.column.index;
    const column = columnsByFileId[mapping.columnTransformation.column.fileId]?.find(
      (c) => c.index === columnIndex,
    );
    preValue = column?.samples?.[0] || '';
    postValue = preValue; // Start with same value
  } else if (
    'pivotColumn' in mapping.columnTransformation &&
    mapping.columnTransformation.pivotColumn
  ) {
    // For pivot columns, show the pivot key value and look up sample from pivot columns
    const pivotCol = (
      mapping.columnTransformation as {
        type: 'PIVOT_COLUMN';
        pivotColumn: {name: string; fileId: string};
      }
    ).pivotColumn;
    preValue = pivotCol.name;
    const pivotColumns = columnsByFileId[pivotCol.fileId]?.filter((c) => c.index < 0) || [];
    const matchingPivot = pivotColumns.find((c) => c.name === pivotCol.name);
    postValue = matchingPivot?.samples?.[0] || '';
  } else if (
    mapping.columnTransformation.type !== 'NONE' &&
    'columns' in mapping.columnTransformation &&
    mapping.columnTransformation.columns?.length > 0
  ) {
    const columnInfo: {name: string; sample: string | null}[] =
      mapping.columnTransformation.columns.map((col) => {
        const column = columnsByFileId[col.fileId]?.find((c) => c.index === col.index);
        return {
          name: column?.name || col.name,
          sample: column?.samples?.[0] ?? null,
        };
      });

    const preValueParts = columnInfo.map((c) => (c.sample ? c.sample : `[${c.name}]`));
    preValue = preValueParts.join(', ');

    const samples = columnInfo.filter((c) => c.sample).map((c) => c.sample as string);
    const columnsWithoutSamples = columnInfo.filter((c) => !c.sample).length;

    switch (mapping.columnTransformation.type) {
      case 'COLUMN_TEXT_CONCATENATION': {
        postValue = preValueParts.join(' ');
        break;
      }
      case 'COLUMN_FORMULA': {
        const numbers = samples
          .map((s: string) => parseFloat(convertAccountingFormat(s).replace(/[^0-9.-]/g, '')))
          .map((n: number) => (isNaN(n) || !isFinite(n) ? 0 : n));
        const formula = mapping.columnTransformation.formula;
        const result = evalTransformationFormula(formula, numbers);
        postValue = Number.isFinite(result) ? String(result) : '';
        if (columnsWithoutSamples > 0 && postValue) {
          postValue += ` (+ ${columnsWithoutSamples} more)`;
        }
        break;
      }
      case 'COLUMN_ADDITION': {
        const numbers = samples
          .map((s: string) => parseFloat(convertAccountingFormat(s).replace(/[^0-9.-]/g, '')))
          .filter((n: number) => !isNaN(n));
        if (numbers.length > 0) {
          postValue = numbers.reduce((a: number, b: number) => a + b, 0).toString();
          if (columnsWithoutSamples > 0) {
            postValue += ` (+ ${columnsWithoutSamples} more)`;
          }
        } else if (columnsWithoutSamples > 0) {
          postValue = `(${columnsWithoutSamples} columns)`;
        } else {
          postValue = '';
        }
        break;
      }
      case 'COLUMN_SUBTRACTION': {
        const numbers = samples
          .map((s: string) => parseFloat(convertAccountingFormat(s).replace(/[^0-9.-]/g, '')))
          .filter((n: number) => !isNaN(n));
        if (numbers.length > 0) {
          const result = numbers.slice(1).reduce((a: number, b: number) => a - b, numbers[0]);
          postValue = isNaN(result) ? '' : result.toString();
        } else {
          postValue = '';
        }
        break;
      }
      case 'COLUMN_MULTIPLICATION': {
        const numbers = samples
          .map((s: string) => parseFloat(convertAccountingFormat(s).replace(/[^0-9.-]/g, '')))
          .filter((n: number) => !isNaN(n));
        postValue =
          numbers.length > 0 ? numbers.reduce((a: number, b: number) => a * b, 1).toString() : '';
        break;
      }
      case 'COLUMN_DIVISION': {
        const numbers = samples
          .map((s: string) => parseFloat(convertAccountingFormat(s).replace(/[^0-9.-]/g, '')))
          .filter((n: number) => !isNaN(n));
        if (numbers.length > 0) {
          const result = numbers
            .slice(1)
            .reduce((a: number, b: number) => (b === 0 ? NaN : a / b), numbers[0]);
          postValue = isNaN(result) ? '' : result.toString();
        } else {
          postValue = '';
        }
        break;
      }
      case 'COLUMN_AVERAGE': {
        const avgNumbers = samples
          .map((s: string) => parseFloat(convertAccountingFormat(s).replace(/[^0-9.-]/g, '')))
          .filter((n: number) => !isNaN(n));
        postValue =
          avgNumbers.length > 0
            ? (avgNumbers.reduce((a: number, b: number) => a + b, 0) / avgNumbers.length).toString()
            : '';
        break;
      }
      case 'COLUMN_MAXIMUM': {
        const maxNumbers = samples
          .map((s: string) => parseFloat(convertAccountingFormat(s).replace(/[^0-9.-]/g, '')))
          .filter((n: number) => !isNaN(n));
        postValue = maxNumbers.length > 0 ? Math.max(...maxNumbers).toString() : '';
        break;
      }
      case 'COLUMN_MINIMUM': {
        const minNumbers = samples
          .map((s: string) => parseFloat(convertAccountingFormat(s).replace(/[^0-9.-]/g, '')))
          .filter((n: number) => !isNaN(n));
        postValue = minNumbers.length > 0 ? Math.min(...minNumbers).toString() : '';
        break;
      }
      case 'COLUMN_FALLBACK': {
        const firstNonEmpty = samples.find(
          (s) => String(s ?? '').trim() !== '' && !isBlankLikeValue(String(s ?? '')),
        );
        postValue = firstNonEmpty || '';
        break;
      }
      default: {
        postValue = preValue;
        break;
      }
    }
  }

  // Apply value transformations
  if (mapping.valueTransformations && mapping.valueTransformations.length > 0) {
    let transformedValue = postValue;

    for (const vt of mapping.valueTransformations) {
      switch (vt.type) {
        case 'TRIM': {
          transformedValue = transformedValue.replace(/\s+/g, '');
          break;
        }
        case 'TO_UPPERCASE': {
          transformedValue = transformedValue.toUpperCase();
          break;
        }
        case 'TO_LOWERCASE': {
          transformedValue = transformedValue.toLowerCase();
          break;
        }
        case 'REMOVE_NON_NUMERIC': {
          transformedValue = transformedValue.replace(/[^0-9]/g, '');
          break;
        }
        case 'REMOVE_NON_ALPHABETIC': {
          transformedValue = transformedValue.replace(/[^a-zA-Z]/g, '');
          break;
        }
        case 'PARSE_NUMBER': {
          const num = parseFloat(
            convertAccountingFormat(transformedValue).replace(/[^0-9.-]/g, ''),
          );
          transformedValue = isNaN(num) ? '' : num.toString();
          break;
        }
        case 'VALUE_MAPPING': {
          if (vt.mappings) {
            const mapping = vt.mappings.find((m) => m.from === transformedValue);
            if (mapping) {
              transformedValue = mapping.to;
            }
          }
          break;
        }
        case 'DEFAULT_IF_EMPTY': {
          if (String(transformedValue ?? '').trim() === '') {
            transformedValue = vt.defaultValue;
          }
          break;
        }
        case 'DEFAULT_UNLESS_VALUE': {
          if (transformedValue !== vt.unlessValue) {
            transformedValue = vt.defaultValue;
          }
          break;
        }
        case 'CONVERT_BLANK_TO_EMPTY': {
          if (isBlankLikeValue(transformedValue)) {
            transformedValue = '';
          }
          break;
        }
        case 'PAD_STRING': {
          if (transformedValue.length < vt.targetLength) {
            const padCount = vt.targetLength - transformedValue.length;
            const padding = vt.padCharacter.repeat(padCount);
            transformedValue =
              vt.padPosition === 'START' ? padding + transformedValue : transformedValue + padding;
          }
          break;
        }
        case 'SUBSTRING': {
          if (transformedValue.length > 0) {
            let start: number;
            if (vt.startIndex > 0) {
              start = vt.startIndex - 1;
            } else {
              start = transformedValue.length + vt.startIndex;
            }
            start = Math.max(0, Math.min(start, transformedValue.length));
            const len = vt.length ?? transformedValue.length - start;
            transformedValue = transformedValue.substring(start, start + len);
          }
          break;
        }
        case 'SPLIT_AND_EXTRACT': {
          if (transformedValue.length > 0) {
            const parts = transformedValue.split(vt.delimiter);
            if (parts.length > 0) {
              let partIdx: number;
              if (vt.partIndex > 0) {
                partIdx = vt.partIndex - 1;
              } else {
                partIdx = parts.length + vt.partIndex;
              }
              if (partIdx >= 0 && partIdx < parts.length) {
                transformedValue = parts[partIdx].trim();
              } else {
                transformedValue = '';
              }
            }
          }
          break;
        }
        case 'NONE': {
          break;
        }
        default: {
          const _exhaustiveCheck: never = vt;
          break;
        }
      }
    }

    postValue = transformedValue;
  }

  return {pre: truncatePreviewValue(preValue), post: truncatePreviewValue(postValue)};
}
